import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Circular, CircularCreate, CircularUpdate } from '@core/types/circular.type';
import { CircularService } from '@core/services';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class CircularQuery {
  private readonly service = inject(CircularService);
  private readonly queryClient = inject(QueryClient);

  useCircularsGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.circulars.grid(c),
        queryFn: () => lastValueFrom(this.service.grid(c)),
        select: (res: ApiResponse<PaginationResult<Circular>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateCircular() {
    return injectMutation(() => ({
      mutationFn: (data: CircularCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.circulars.all }),
    }));
  }

  useUpdateCircular() {
    return injectMutation(() => ({
      mutationFn: (data: CircularUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.circulars.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.circulars.all });
      },
    }));
  }

  useDeleteCircular() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.circulars.all }),
    }));
  }
}

